import { forwardRef } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import Location from '../../common/Icons/Location';
import ListItem from './ListItem';
import testIds from './testIds';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import HotelFromApi from '../../../../types/HotelFromApi';
import isIos from '../../../core/helpers/isIos';

const Mapbox = dynamic(() => import('../../common/Mapbox'), { ssr: false });
const MapboxStatic = dynamic(() => import('../../common/Mapbox/MapboxStatic'));

const CONTAINER_DIV_ID = 'map_hotel_access';

type Props = {
  hotel: HotelFromApi | undefined;
};

const Access = forwardRef<HTMLDivElement, Props>(({ hotel }, ref) => {
  const { t } = useTranslation('hotel');
  const latitude = encodeURIComponent(String(hotel?.address?.latitude ?? ''));
  const longitude = encodeURIComponent(String(hotel?.address?.longitude ?? ''));
  const encodedName = encodeURIComponent(hotel?.name ?? '');
  const mapsQuery = `ll=${latitude},${longitude}&q=${encodedName}`;

  const events = {
    onClickMap: () => {
      if (isIos())
        // even if ios device don't have google map it will redirect to apple map instead
        return window.open(
          `maps://maps.google.com/maps?${mapsQuery}`,
          '_blank',
          'noopener,noreferrer'
        );
      return window.open(
        `https://maps.google.com/maps?${mapsQuery}`,
        '_blank',
        'noopener,noreferrer'
      );
    },
  };

  return (
    <div id="access" ref={ref} className="scroll-mt-8">
      <p className="app-section-title">{t('access.title')}</p>
      <div className="flex pb-4">
        <Location size={24} />
        <p data-testid={testIds.hotel_postal_address} className="pl-2 text-small sm:text-regular">
          {hotel?.address?.postalAddress}
        </p>
      </div>
      {useIsAboveBreakpoint('SM') ? (
        <div className="relative mb-6 h-72 sm:mb-8 xl:h-84">
          <Mapbox
            longitude={longitude}
            latitude={latitude}
            containerDivId={CONTAINER_DIV_ID}
            className="h-full"
          />
        </div>
      ) : (
        <button
          type="button"
          className="mb-4 w-full cursor-pointer self-start"
          onClick={() => events.onClickMap()}
        >
          <MapboxStatic longitude={longitude} latitude={latitude} width={500} height={500} />
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        {hotel?.accessSimple && (
          <ListItem data-testid={testIds.hotel_access} type="access" text={hotel?.accessSimple} />
        )}
        {hotel?.tel && <ListItem data-testid={testIds.hotel_tel} type="tel" text={hotel?.tel} />}
        {hotel?.touristHighlights && hotel?.touristHighlights?.length > 0 && (
          <ListItem
            data-testid={testIds.hotel_sightseeing}
            type="sightseeing"
            text={hotel?.touristHighlights.join(' / ')}
          />
        )}
      </div>
    </div>
  );
});

Access.displayName = 'Access';

export default Access;
